import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBrands } from '../../hooks/useApi'
import { useMarketingPlanner, useMarketingAccess } from '../../hooks/useMarketing'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { Wand2, Crown, Lightbulb } from 'lucide-react'
import { normalizePlanId } from '../../lib/plans'

export default function MarketingPlanner() {
  const { data: access } = useMarketingAccess()
  const plan = normalizePlanId(access?.plan)
  const isPremium = plan === 'premium'
  const { data: brands } = useBrands()
  const planner = useMarketingPlanner()

  const [brandId, setBrandId] = useState<number | ''>('')
  const [recommendations, setRecommendations] = useState<Array<{
    type: string
    title: string
    description: string
    productSuggestion?: string
    promoType?: string
  }>>([])

  if (!isPremium) {
    return (
      <div className="text-center py-16">
        <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold">Marketing Planner — Premium</h2>
        <p className="text-gray-600 mt-2 mb-4">Dapatkan rekomendasi promosi dari AI</p>
        <Link to="/subscription" className="text-indigo-600 font-medium hover:underline">Upgrade ke Premium →</Link>
      </div>
    )
  }

  const handleGenerate = async () => {
    if (!brandId) return
    const res = await planner.mutateAsync({ brandId: Number(brandId) })
    setRecommendations(res.recommendations)
  }

  const typeLabels: Record<string, string> = {
    produk_lama: 'Produk Lama',
    minggu_ini: 'Minggu Ini',
    hari_besar: 'Hari Besar',
    musim: 'Musim',
    kategori: 'Kategori',
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Marketing Planner</h1>
        <p className="text-gray-600 mt-1">Rekomendasi promosi dari AI (1 AI Boost)</p>
      </div>

      <Card>
        <CardContent className="pt-6 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-1">Brand</label>
            <select className="w-full border rounded-lg px-3 py-2" value={brandId} onChange={(e) => setBrandId(Number(e.target.value) || '')}>
              <option value="">Pilih Brand</option>
              {brands?.map((b: { id: number; name: string }) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <Button onClick={handleGenerate} disabled={!brandId || planner.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Wand2 className="w-4 h-4 mr-2" />
            Dapatkan Rekomendasi
          </Button>
        </CardContent>
      </Card>

      {recommendations.length > 0 && (
        <div className="grid gap-4">
          {recommendations.map((rec, idx) => (
            <Card key={idx}>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-indigo-600 uppercase">
                      {typeLabels[rec.type] || rec.type}
                    </span>
                    <h3 className="font-bold text-gray-900 mt-1">{rec.title}</h3>
                    <p className="text-sm text-gray-600 mt-2">{rec.description}</p>
                    {rec.productSuggestion && (
                      <p className="text-sm text-gray-500 mt-2">Produk: <strong>{rec.productSuggestion}</strong></p>
                    )}
                    {rec.promoType && (
                      <p className="text-sm text-gray-500">Jenis promo: <strong>{rec.promoType.replace(/_/g, ' ')}</strong></p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
